import React, { Component } from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import i18n from 'i18next'
import Checkbox from '../../../form/Checkbox'
import CheckboxGroup from '../../../form/CheckboxGroup'
import Input from '../../../form/Input'
import Select from '../../../form/Select'
import ColorPicker from '../../../../UniformComponents/ColorPicker'
import Slider from '../../../../UniformComponents/Slider'
import Typography from '../../../../UniformComponents/Typography'

const defaultColors = [
    '#97D100',
    '#FFDA10',
    '#FEB200',
    '#FD597F',
    '#D9314B',
    '#16CCC1',
]

const barDefaultColors = [
    '#16CCC1',
    '#715AF8',
    '#1A81F0',
    '#97D100',
    '#FFDA10',
    '#FEB200',
    '#FD597F',
    '#D9314B',
]

const sortByOptions = [
    { label: 'None', value: 'none' },
    { label: 'Name', value: 'name' },
    { label: 'Event End Date', value: 'endDate' },
    { label: 'Value', value: 'value' },
    { label: 'Group', value: 'tagOptionName' },
]

const sortDirections = [
    { label: 'Ascending', value: 1 },
    { label: 'Descending', value: -1 },
]

const formatOptions = [
    { label: 'Numeric', value: 'numeric' },
    { label: 'Currency', value: 'currency' },
    { label: '%', value: '%' },
]

const removeZeroNull = [
    { label: 'Do Not Remove', value: 'dontRemove' },
    { label: 'Remove Zeros', value: 'zero' },
    { label: 'Remove Null', value: 'null' },
    { label: 'Remove Both', value: 'both' },
]

const currencyOptions = [
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' },
    { label: 'EUR', value: 'EUR' },
    { label: 'AUD', value: 'AUD' },
]

const getSettingOptions = () => [
    {
        name: 'legend',
        label: i18n.t('dashboards.widgetDisplayOptions.showLegend'),
        widgetRenderPath: 'showLegend',
        defaultValue: true,
        type: 'checkbox',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'pie', 'donut', 'radar', 'line', 'quadrantAnalysis'],
        group: 'legend',
    },
    {
        name: 'boundaryGap',
        label: i18n.t('dashboards.widgetDisplayOptions.boundaryGap'),
        widgetRenderPath: 'boundaryGap',
        defaultValue: true,
        type: 'checkbox',
        allowedCharts: ['line'],
        group: 'style',
    },
    {
        name: 'showOptionLabels',
        label: i18n.t('dashboards.widgetDisplayOptions.showOptionLabels'),
        widgetRenderPath: 'showOptionLabels',
        defaultValue: false,
        type: 'checkbox',
        allowedCharts: ['pie', 'donut'],
        group: 'labels',
    },
    {
        name: 'showOptionLabels',
        label: i18n.t('dashboards.widgetDisplayOptions.showOptionLabels'),
        widgetRenderPath: 'showOptionLabels',
        defaultValue: true,
        type: 'checkbox',
        allowedCharts: ['treemap'],
        group: 'labels',
    },
    {
        name: 'showDataLabels',
        label: i18n.t('dashboards.widgetDisplayOptions.showDataLabels'),
        widgetRenderPath: 'showDataLabels',
        defaultValue: true,
        type: 'checkbox',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'pie', 'donut', 'radar', 'treemap', 'line'],
        group: 'labels',
    },
    {
        name: 'hideOverlapLabels',
        label: i18n.t('dashboards.widgetDisplayOptions.hideOverlappingLabels'),
        widgetRenderPath: 'hideOverlapLabels',
        defaultValue: false,
        type: 'checkbox',
        allowedCharts: ['pie', 'donut'],
        group: 'labels',
    },
    {
        name: 'labelPosition',
        label: i18n.t('dashboards.widgetDisplayOptions.labelPosition'),
        widgetRenderPath: 'labelPosition',
        defaultValue: 'inside',
        options: [{ label: 'Inside', value: 'inside' }, { label: 'Outside', value: 'outside' }],
        type: 'select',
        allowedCharts: ['pie', 'donut'],
        show: (widget) => (R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
            || R.pathOr(false, ['widgetRenderSettings', 'showOptionLabels'], widget)),
        group: 'labels',
    },
    {
        name: 'labelPosition',
        label: i18n.t('dashboards.widgetDisplayOptions.labelPosition'),
        widgetRenderPath: 'labelPosition',
        defaultValue: 'inside',
        options: [{ label: 'Inside', value: 'inside' }, { label: 'Top', value: 'top' }, { label: 'Inside Top', value: 'insideTop' }],
        type: 'select',
        allowedCharts: ['column', 'stackedColumn'],
        show: (widget) => R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget),
        group: 'labels',
    },
    {
        name: 'labelPosition',
        label: i18n.t('dashboards.widgetDisplayOptions.labelPosition'),
        widgetRenderPath: 'labelPosition',
        defaultValue: 'inside',
        options: [{ label: 'Inside', value: 'inside' }, { label: 'Right', value: 'right' }, { label: 'Inside Right', value: 'insideRight' }],
        type: 'select',
        allowedCharts: ['bar', 'stackedBar'],
        show: (widget) => R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget),
        group: 'labels',
    },
    {
        name: 'labelPosition',
        label: i18n.t('dashboards.widgetDisplayOptions.labelPosition'),
        widgetRenderPath: 'labelPosition',
        defaultValue: 'top',
        options: [{ label: 'Top', value: 'top' }, { label: 'Bottom', value: 'bottom' }],
        type: 'select',
        allowedCharts: ['line'],
        show: (widget) => R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget),
        group: 'labels',
    },
    {
        name: 'rotateXAxisLabels',
        label: i18n.t('dashboards.widgetDisplayOptions.rotateXLabels'),
        widgetRenderPath: 'rotateXAxisLabels',
        defaultValue: (widget) => R.pathOr(
            widget.xAxisLabels?.length > 8,
            ['widgetRenderSettings', 'rotateXAxisLabels'],
            widget,
        ),
        type: 'checkbox',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        group: 'labels',
    },
    {
        name: 'orientation',
        label: i18n.t('dashboards.widgetDisplayOptions.orientation'),
        widgetRenderPath: 'orientation',
        defaultValue: (widget) => R.pathOr(
            Number(R.pathOr(2, ['width'], widget)) < 4 ? 'vertical' : 'horizontal',
            ['widgetRenderSettings', 'orientation'],
            widget,
        ),
        options: [{ label: 'Vertical', value: 'vertical' }, { label: 'Horizontal', value: 'horizontal' }],
        type: 'select',
        allowedCharts: ['pie', 'donut'],
        group: 'layout',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'swapSeriesXAxis',
        label: i18n.t('dashboards.widgetDisplayOptions.swapSeriesXAxis'),
        widgetRenderPath: 'swapSeriesXAxis',
        defaultValue: (widget) => R.pathOr(
            false,
            ['widgetRenderSettings', 'swapSeriesXAxis'],
            widget,
        ),
        type: 'checkbox',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        group: 'dataSettings',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'format',
        label: i18n.t('dashboards.widgetDisplayOptions.format'),
        widgetRenderPath: 'format',
        options: formatOptions,
        defaultValue: (widget) => R.pathOr(widget.isCurrency ? 'currency' : 'numeric',
            ['widgetRenderSettings', 'format'],
            widget),
        type: 'select',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'pie', 'donut', 'line', 'text', 'quadrantAnalysis', 'treemap'],
        group: 'dataSettings',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'currency',
        label: i18n.t('currency'),
        widgetRenderPath: 'currency',
        options: currencyOptions,
        defaultValue: (widget) => R.pathOr('USD',
            ['widgetRenderSettings', 'currency'],
            widget),
        type: 'select',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'pie', 'donut', 'text', 'quadrantAnalysis', 'gauge', 'treemap'],
        show: (widget, showAdvancedSettings) => {
            if (!showAdvancedSettings) return false
            return R.pathOr(widget.isCurrency ? 'currency' : '', ['widgetRenderSettings', 'format'], widget) === 'currency'
        },
        group: 'dataSettings',
    },
    {
        name: 'showTitle',
        label: i18n.t('dashboards.widgetDisplayOptions.showTitle'),
        widgetRenderPath: 'showTitle',
        defaultValue: false,
        type: 'checkbox',
        onBlur: (control, value, changeSettings, widget) => {
            changeSettings(control, value)
            if (value) {
                changeSettings(
                    { widgetRenderPath: 'title' },
                    R.pathOr('', ['question', 'content', 'longTitle'], widget),
                )
            }
        },
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'pie', 'donut', 'radar', 'treemap', 'line', 'quadrantAnalysis', 'textResponse'],
        group: 'title',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'title',
        label: i18n.t('dashboards.widgetDisplayOptions.title'),
        widgetRenderPath: 'title',
        defaultValue: (widget) => R.pathOr(
            R.pathOr('', ['question', 'content', 'longTitle', widget.langcode, 'text'], widget),
            ['widgetRenderSettings', 'title', widget.langcode, 'text'],
            widget,
        ),
        type: 'input',
        onBlur: (control, value, changeSettings, widget) => {
            changeSettings(
                { widgetRenderPath: 'title' },
                {
                    ...R.pathOr(
                        {},
                        ['widgetRenderSettings', 'title'],
                        widget,
                    ),
                    [widget.langcode]: { text: value },
                },
            )
        },
        show: (widget, showAdvancedSettings) => {
            if (!showAdvancedSettings) return false
            return R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
        },
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'pie', 'donut', 'radar', 'treemap', 'line', 'quadrantAnalysis', 'textResponse'],
        group: 'title',
    },
    {
        name: 'sortBy',
        label: i18n.t('dashboards.widgetDisplayOptions.sortBy'),
        widgetRenderPath: 'sortBy',
        options: (widget) => sortByOptions
            .filter((o) => !(widget?.dashOid === 6 && ['endDate', 'tagOptionName'].includes(o.value))),
        defaultValue: (widget) => R.pathOr(widget?.scope === 'product' && widget?.dashOid !== 6 ? 'endDate' : 'none',
            ['widgetRenderSettings', 'sortBy'],
            widget),
        type: 'select',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        group: 'dataSettings',
    },
    {
        name: 'sortDirection',
        label: i18n.t('dashboards.widgetDisplayOptions.sortDirection'),
        widgetRenderPath: 'sortDirection',
        options: sortDirections,
        defaultValue: (widget) => R.pathOr(1,
            ['widgetRenderSettings', 'sortDirection'],
            widget),
        type: 'select',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        group: 'dataSettings',
    },
    {
        name: 'showYAxis',
        label: i18n.t('dashboards.widgetDisplayOptions.showYAxis'),
        widgetRenderPath: 'showYAxis',
        defaultValue: true,
        type: 'checkbox',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        group: 'style',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'scrollable',
        label: i18n.t('dashboards.widgetDisplayOptions.scrollable'),
        widgetRenderPath: 'scrollable',
        defaultValue: false,
        type: 'checkbox',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        group: 'style',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'areaFill',
        label: i18n.t('dashboards.widgetDisplayOptions.areaFill'),
        widgetRenderPath: 'areaFill',
        defaultValue: false,
        type: 'checkbox',
        allowedCharts: ['radar'],
        group: 'style',
    },
    {
        name: 'colors',
        label: i18n.t('dashboards.widgetDisplayOptions.colors'),
        key: 'colors',
        defaultOptions: (widget) => (R.pathOr([], ['fData', 0, 'data'], widget).map((d, i) => defaultColors[i % defaultColors.length])),
        onBlur: (widget, value, index, changeSettings, control) => {
            const newColors = R.pathOr([], ['fData', 0, 'data'], widget).map((dColor, i) => (i === index
                ? value
                : R.pathOr(defaultColors[i % defaultColors.length], ['widgetRenderSettings', 'colors', i], widget)))
            changeSettings(control, newColors)
        },
        type: 'colorPicker',
        widgetRenderPath: 'colors',
        allowedCharts: ['pie', 'donut'],
        group: 'optionSettings',
    },
    {
        name: 'colors',
        label: i18n.t('dashboards.widgetDisplayOptions.colors'),
        key: 'colors',
        defaultOptions: (widget) => (R.pathOr([], ['fData'], widget).map((d, i) => barDefaultColors[i % barDefaultColors.length])),
        onBlur: (widget, value, index, changeSettings, control) => {
            const newColors = R.pathOr([], ['fData'], widget).map((dColor, i) => (i === index
                ? value
                : R.pathOr(barDefaultColors[i % barDefaultColors.length], ['widgetRenderSettings', 'colors', i], widget)))
            changeSettings(control, newColors)
        },
        type: 'colorPicker',
        widgetRenderPath: 'colors',
        allowedCharts: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        group: 'optionSettings',
    },
    {
        name: 'colors',
        label: i18n.t('dashboards.widgetDisplayOptions.colors'),
        key: 'colors',
        defaultOptions: () => (['#16CCC1']),
        onBlur: (widget, value, index, changeSettings, control) => {
            const newColors = ['#16CCC1'].map((dColor, i) => (i === index
                ? value
                : R.pathOr('#16CCC1', ['widgetRenderSettings', 'colors', i], widget)))
            changeSettings(control, newColors)
        },
        type: 'colorPicker',
        widgetRenderPath: 'colors',
        allowedCharts: ['textResponse'],
        group: 'optionSettings',
    },
    {
        name: 'removeZeroNull',
        label: i18n.t('dashboards.widgetDisplayOptions.removeNullValues'),
        widgetRenderPath: 'removeZeroNull',
        options: removeZeroNull,
        defaultValue: (widget) => R.pathOr('dontRemove',
            ['widgetRenderSettings', 'removeZeroNull'],
            widget),
        type: 'select',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'pie', 'donut', 'radar', 'treemap', 'line'],
        group: 'dataSettings',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'dataDecimals',
        label: i18n.t('dashboards.widgetDisplayOptions.decimalPlaces'),
        widgetRenderPath: 'dataDecimals',
        options: [{ label: '0', value: 0 }, { label: '1', value: 1 }, { label: '2', value: 2 }, { label: '3', value: 3 }, { label: '4', value: 4 }],
        defaultValue: (widget) => R.pathOr(0,
            ['widgetRenderSettings', 'dataDecimals'],
            widget),
        type: 'select',
        allowedCharts: ['gauge', 'text', 'lmx', 'column', 'bar', 'stackedBar', 'stackedColumn', 'pie', 'donut', 'radar', 'treemap', 'line', 'multiText'],
        group: 'dataSettings',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'dataDecimals',
        label: i18n.t('dashboards.widgetDisplayOptions.decimalPlaces'),
        widgetRenderPath: 'dataDecimals',
        options: [{ label: '0', value: 0 }, { label: '1', value: 1 }, { label: '2', value: 2 }, { label: '3', value: 3 }, { label: '4', value: 4 }],
        defaultValue: (widget) => R.pathOr(2,
            ['widgetRenderSettings', 'dataDecimals'],
            widget),
        type: 'select',
        allowedCharts: ['gauge0-5'],
        group: 'dataSettings',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'allowedBenchmarks',
        label: i18n.t('dashboards.widgetDisplayOptions.benchmarkSettings'),
        widgetRenderPath: 'allowedBenchmarks',
        options: (widget, dashStore) => (
            dashStore.possibleBenchmarks[dashStore.oId].map((b) => (
                { value: b, label: dashStore.benchmarkLabels[b] }
            ))
        ),
        checkboxDefaultValue: (widget, dashStore, benchmark) => {
            const defaultValues = dashStore.possibleBenchmarks[dashStore.oId]
            return R.pathOr(defaultValues,
                ['widgetRenderSettings', 'allowedBenchmarks'],
                widget).includes(benchmark)
        },
        checkboxOnBlur: (changeSettings, benchmark, value, widget, dashStore) => {
            const defaultValues = dashStore.possibleBenchmarks[dashStore.oId]
            const currentValues = [...R.pathOr(defaultValues,
                ['widgetRenderSettings', 'allowedBenchmarks'],
                widget)]
            if (!value) {
                const indexOfBenchmark = currentValues.indexOf(benchmark)
                currentValues.splice(indexOfBenchmark, 1)
                changeSettings({ widgetRenderPath: 'allowedBenchmarks' }, currentValues)
            } else {
                changeSettings({ widgetRenderPath: 'allowedBenchmarks' }, [...currentValues, benchmark])
            }
        },
        selectLabel: 'Period',
        selectName: 'benchmarkPeriods',
        selectOptions: [{ value: 'ROL', label: 'Last 24 months' }, { value: 'ALL', label: 'All time' }],
        selectDefaultValue: (widget, benchmark) => R.pathOr('ROL',
            ['widgetRenderSettings', 'benchmarkPeriods', benchmark],
            widget),
        selectOnBlur: (changeSettings, benchmark, value, widget, dashStore) => {
            const currentValues = R.pathOr({},
                ['widgetRenderSettings', 'benchmarkPeriods'],
                widget)
            changeSettings({ widgetRenderPath: 'benchmarkPeriods' }, { ...currentValues, [benchmark]: value })
        },
        type: 'checkboxSelectGroup',
        allowedCharts: [],
        group: 'Benchmark Settings',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
    {
        name: 'allowedBenchmarks',
        label: i18n.t('dashboards.widgetDisplayOptions.allowedBenchmarks'),
        widgetRenderPath: 'allowedBenchmarks',
        options: (widget, dashStore) => dashStore.possibleBenchmarks[dashStore.oId].map((b) => (
            { value: b, label: dashStore.benchmarkLabels[b] }
        )),
        defaultValue: (widget, dashStore) => {
            const defaultValues = dashStore.possibleBenchmarks[dashStore.oId]
            return R.pathOr(defaultValues,
                ['widgetRenderSettings', 'allowedBenchmarks'],
                widget)
        },
        type: 'checkboxGroup',
        allowedCharts: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        group: 'Benchmark Settings',
        show: (widget, showAdvancedSettings) => (showAdvancedSettings),
    },
]

@observer
class DisplayOptions extends Component {
    constructor(props) {
        super(props)
        this.state = {
            openAccordian: '',
            showAdvancedSettings: false,
        }
    }

    changeSettings = (control, value) => {
        const { dashboardWizardStore, updatePreview } = this.props
        const {
            newWidget,
        } = dashboardWizardStore
        const { changeNewWidgetBulk } = newWidget

        const widgetRenderSettings = {
            ...newWidget.widgetRenderSettings,
            [control.widgetRenderPath]: value,
        }
        changeNewWidgetBulk([{ value: widgetRenderSettings, field: 'widgetRenderSettings' }])
        updatePreview()
    }

    editInputList = (prevName, value, options, control) => {
        const { dashboardWizardStore, updatePreview } = this.props
        const {
            newWidget,
        } = dashboardWizardStore
        const { changeNewWidgetBulk } = newWidget

        const optionValue = options.reduce((obj, o) => ({
            ...obj,
            [o]: o === prevName
                ? value
                : R.pathOr(o, ['widgetRenderSettings', control.name, o], newWidget),
        }), {})

        const widgetRenderSettings = {
            ...newWidget.widgetRenderSettings,
            [control.widgetRenderPath]: optionValue,
        }
        changeNewWidgetBulk([{ value: widgetRenderSettings, field: 'widgetRenderSettings' }])
        updatePreview()
    }

    renderControls = (control) => {
        const { dashboardWizardStore, widgetWithData } = this.props
        const { dashStore, newWidget } = dashboardWizardStore
        const { appStore } = dashStore
        const { langcode } = appStore
        const { formatData } = dashStore

        if (!widgetWithData) return null
        const { fData, xAxisLabels } = formatData(widgetWithData.data, widgetWithData)
        const widget = {
            ...widgetWithData,
            fData,
            xAxisLabels,
            question: newWidget.metrics[0]?.question,
            langcode,
            dashOid: dashStore.oId,
        }

        return {
            checkbox: () => (
                <div style={{ margin: '8px 0' }} key={control.name}>
                    <Slider
                        label={control.label}
                        name={control.name}
                        onChange={({ value }) => (control.onBlur && typeof control.onBlur === 'function'
                            ? control.onBlur(control, value, this.changeSettings, widget)
                            : this.changeSettings(control, value))}
                        defaultChecked={typeof control.defaultValue === 'function' ? control.defaultValue(widget) : R.pathOr(
                            control.defaultValue,
                            ['widgetRenderSettings', control.widgetRenderPath],
                            newWidget,
                        )}
                        value={R.pathOr(undefined, ['widgetRenderSettings', control.name], newWidget)}
                    />
                </div>
            ),
            checkboxGroup: () => (
                <div style={{ margin: '8px 0' }} key={control.name}>
                    <CheckboxGroup
                        label={control.label}
                        name={control.name}
                        key={control.key}
                        options={control.options(widget, dashStore)}
                        onBlur={({ value }) => this.changeSettings(control, value)}
                        defaultChecked={control.defaultValue(widget, dashStore)}
                        returnValueUnchanged
                    />
                </div>
            ),
            inputList: () => (
                <div key={control.name}>
                    <Typography type="b" style={{ margin: '4px 0 8px 0' }}>
                        {control.label}
                    </Typography>
                    <div style={{
                        marginBottom: '16px', overflow: 'auto', maxHeight: '200px', padding: '16px 8px', borderRadius: '8px', border: '0.3px solid #C7CFD8',
                    }}
                    >
                        {control.options(widget).map((field, i) => (
                            <div style={{
                                marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px', borderBottom: '1px solid #C7CFD8', paddingBottom: '4px',
                            }}
                            >
                                <Input
                                    label={field.label}
                                    name={field.name}
                                    key={control.key + field.label}
                                    defaultValue={field.value}
                                    onBlur={(value) => control.onBlur(
                                        value, widget, control, this.editInputList,
                                    )}
                                />
                                <Input
                                    label={`Default - ${field.label}`}
                                    name={`${field.name}default`}
                                    key={`${control.key + field.label}default`}
                                    defaultValue={control.defaultOptions(widget)[i]}
                                    disabled
                                />
                            </div>
                        ))}

                    </div>
                </div>
            ),
            select: () => (
                <div style={{ marginTop: '16px', marginBottom: '8px', position: 'relative' }} key={control.name}>
                    <Select
                        label={control.label}
                        classNamePrefix="relative"
                        name={control.name}
                        options={typeof control.options === 'function' ? control.options(widget) : control.options}
                        defaultValue={typeof control.defaultValue === 'function'
                            ? control.defaultValue(widget)
                            : R.pathOr(control.defaultValue, ['widgetRenderSettings', control.widgetRenderPath], widget)}
                        onBlur={({ value }) => this.changeSettings(control, value)}
                        blurOnClear
                        value={(typeof control.options === 'function' ? control.options(widget) : control.options)
                            .find((v) => v.value === R.pathOr(undefined, ['widgetRenderSettings', control.name], newWidget)) || undefined}
                    />
                </div>
            ),
            input: () => (
                <div style={{ margin: '16px 0', position: 'relative' }} key={control.name}>
                    <Input
                        label={control.label}
                        name={control.name}
                        defaultValue={typeof control.defaultValue === 'function' ? control.defaultValue(widget) : R.pathOr(
                            control.defaultValue,
                            ['widgetRenderSettings', control.widgetRenderPath],
                            widget,
                        )}
                        onBlur={({ value }) => (control.onBlur && typeof control.onBlur === 'function'
                            ? control.onBlur(control, value, this.changeSettings, widget)
                            : this.changeSettings(control, value))}
                    />
                </div>
            ),
            colorPicker: () => (
                <div style={{ margin: '16px 0', position: 'relative' }} key={control.name}>
                    <Typography type="b" style={{ margin: '4px 0 8px 0' }}>
                        {control.label}
                    </Typography>
                    {control.defaultOptions(widget).map((c, i) => (
                        <div className="colorInputContainer">
                            <ColorPicker
                                name={i}
                                defaultValue={c}
                                onChange={(value, key) => {
                                    control.onBlur(widget, value, Number(key), this.changeSettings, control)
                                }}
                            />
                            <div className="colorPreview" style={{ backgroundColor: R.pathOr(c, ['widgetRenderSettings', 'colors', i], widget) }} />
                        </div>
                    ))}

                </div>
            ),
            checkboxSelectGroup: () => (
                <div style={{ margin: '16px 0', position: 'relative' }}>
                    {
                        control.options(widget, dashStore).map((o) => (
                            <div style={{ marginBottom: '16px' }}>
                                <Checkbox
                                    label={o.label}
                                    name={o.value}
                                    onBlur={({ value }) => (
                                        control.checkboxOnBlur(
                                            this.changeSettings, o.value, value, widget, dashStore,
                                        )
                                    )}
                                    checked={control.checkboxDefaultValue(widget, dashStore, o.value)}
                                    style={{ marginBottom: '16px' }}
                                />
                                <Select
                                    label={`${o.label} ${control.selectLabel}`}
                                    name={control.selectName}
                                    options={control.selectOptions}
                                    defaultValue={control.selectDefaultValue(widget, o.value)}
                                    onBlur={({ value }) => (
                                        control.selectOnBlur(
                                            this.changeSettings, o.value, value, widget, dashStore,
                                        )
                                    )}
                                    blurOnClear
                                />
                            </div>
                        ))
                    }
                </div>
            ),
        }[control.type]()
    }

    renderFields = () => {
        const { dashboardWizardStore, widgetWithData } = this.props
        const { dashStore, newWidget } = dashboardWizardStore
        const { chartType } = newWidget
        const { formatData } = dashStore
        if (!widgetWithData) return null
        const { fData, xAxisLabels } = formatData(widgetWithData.data, widgetWithData)
        const widget = {
            ...widgetWithData,
            ...newWidget,
            fData,
            xAxisLabels,
        }
        const { showAdvancedSettings } = this.state

        if (!chartType) return null
        const fields = getSettingOptions().filter((so) => so.allowedCharts.includes(chartType)
            && (so.show === undefined || so.show(widget, showAdvancedSettings)))
        if (!fields || !fields.length) return null

        const groupedFields = R.groupBy((field) => field.group)(fields)
        const renderGroups = this.getRenderGroups(groupedFields, widget, dashStore)

        return Object.keys(groupedFields)
            .filter((groupName) => renderGroups[groupName])
            .map((groupName) => (
                <div className="displayOptionsControlBlock">
                    <h4>{i18n.t(`dashboards.widgetDisplayOptions.${groupName}`)}</h4>
                    <div className="accordianBody chartSizes">
                        {groupedFields[groupName]
                            .filter((f) => f.show === undefined || f.show(widget, showAdvancedSettings))
                            .map((c) => this.renderControls(c))}
                    </div>
                </div>
            ))
    }

    getRenderGroups = (groupFields, widget) => Object.keys(groupFields).reduce((acc, groupName) => {
        const group = groupFields[groupName]
        const render = group.some((field) => {
            if (field.type === 'colorPicker') {
                return typeof field.defaultOptions === 'function'
                    ? field.defaultOptions(widget).length > 0
                    : field.defaultOptions?.length > 0
            }
            if (field.type === 'select') {
                return typeof field.options === 'function'
                    ? field.options(widget).length > 0
                    : field.options?.length > 0
            }
            return true
        })
        return {
            ...acc,
            [groupName]: render,
        }
    }, {})

    render() {
        const { showAdvancedSettings } = this.state
        const { dashboardWizardStore } = this.props
        const { renderWidgetDisplayOptions } = dashboardWizardStore

        return (
            <div className="chartSettings">
                <div style={{ margin: '8px 0 0 0', width: '115px' }} key="showAdvanced">
                    <Slider
                        label={i18n.t('advanced')}
                        name="showAdvanced"
                        onChange={() => this.setState({ showAdvancedSettings: !showAdvancedSettings })}
                        defaultChecked={false}
                    />
                </div>
                {renderWidgetDisplayOptions()}
                {this.renderFields()}
            </div>
        )
    }
}

export default DisplayOptions
